<script setup lang="ts">
import { computed ,ref } from 'vue';

import DialogModal from '@/components/dialogs/DialogModal.vue';
import UpRightAndDownLeftFromCenterSolid from '@/components/svg/UpRightAndDownLeftFromCenterSolid.vue';
import XMarkSolid from '@/components/svg/XMarkSolid.vue';

const props = defineProps<{
  displayUrl: string;
  modalUrl?: string;
}>();

const dialogVisible = ref(false);
const displayImageIsLoading = ref(true);

const imageForModal = computed(() => props.modalUrl ?? props.displayUrl);
</script>

<template>
  <div v-if="displayUrl"
       class="imageLightbox">
    <div class="imageLightbox-container">
      <img :src="displayUrl"
           alt="Preview image"
           class="imageLightbox-image loadingImage"
           :data-image-is-loading="displayImageIsLoading"
           @load="displayImageIsLoading = false"
           @click="dialogVisible = true"
           style="cursor: pointer;">
      <button class="btn btn_expandButton"
              @click="dialogVisible = true">
        <UpRightAndDownLeftFromCenterSolid />
      </button>
    
      <DialogModal v-model:open="dialogVisible"
                   class="dialogModal_imageLightbox"
                   hide-close-button>
        <template #container>
          <div class="imageLightbox-modalContainer">
            <img :src="imageForModal"
                 alt="Full size image"
                 class="imageLightbox-modalImage"
                 loading="lazy">
            <button class="btn imageLightbox-closeButton"
                    @click="dialogVisible = false">
              <XMarkSolid />
            </button>
          </div>
        </template>
      </DialogModal>
    </div>
  </div>
</template>

<style lang="scss">
.imageLightbox {
  position: relative;
  width: 100%;
  
  &-container {
    position: relative;
    width: 100%;
    display:flex;
    justify-content: center;
    
    &:hover .btn_expandButton {
      opacity: .8;
    }
  }
  
  &-image {
    max-width: 100%;
    height: auto;
    display: block;
    cursor: pointer;
    
    @media (hover: none) {
      &:active {
        opacity: 0.8;
        transform: scale(0.98);
        transition: all 0.1s ease;
      }
    }
  }
  
  .btn_expandButton {
    position: absolute;
    top: 8px;
    right: 8px;
    opacity: .9;
    background-color: var(--gray-950);
    color: var(--white);
    z-index: 2;
    
    @media (hover: none) {
      opacity: .9;
      background-color: rgba(0, 0, 0, 0.7);
      border-radius: 4px;
      padding: 8px;
    }

    @media (hover: hover) {
      opacity: 0;
      z-index: 1;
      transition-property: var(--transitionProperties), opacity;
    }
  }
  
  &-modalContainer {
    position: relative;
    width: 100%;
    height: auto;
    max-height: 80vh;
    max-width: 90vw;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow-y: auto;
    overflow-x: hidden;
    border-radius: var(--radius-md);
    border:1.00rem solid #ffffff;
    
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.5);
      border-radius: 4px;
      
      &:hover {
        background: rgba(255, 255, 255, 0.7);
      }
    }

    @media (width <= 768px) {
      max-height: 75vh;
      max-width: 95vw;
    }
    
    @media (width <= 480px) {
      max-height: 80vh;
      max-width: 98vw;
    }
  }
  
  &-modalImage {
    display: block;
    max-width: 100%;
    width: auto;
    height: auto;
    margin: 0 auto;
    border-radius: 8px;
    animation: fadeInScale 0.3s ease-out;
    flex-shrink: 0;

    @media (orientation: landscape) {
      max-width: 85vw;
    }

    @media (width <= 768px) {
      max-width: 90vw;
    }
    
    @media (width <= 480px) {
      max-width: 95vw;
    }
  }
  
  &-closeButton {
    position: fixed;
    top: 2.5rem;
    right: 2.5rem;
    z-index: 1;
    color: #ffffff;
    background-color: var(--gray-950);
    opacity: .8;

    &:hover {
      background-color: var(--gray-950);
      opacity: 1;
    }
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.dialogModal_imageLightbox {
  width: fit-content;
  max-width: 90vw;
  max-height: 80vh;
  padding: 0;
  min-width: 0;
  overflow: hidden;
  
  @media (width <= 768px) {
    max-width: 95vw;
    max-height: 75vh;
    min-width: 0;
  }
  
  @media (width <= 480px) {
    max-width: 98vw;
    max-height: 80vh;
  }
}
</style>
